import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  PageOrientation,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";

import { formatMoney } from "../money";
import type {
  BillingCadence,
  GeneratedQuotationFile,
  Quotation,
  QuotationExportSnapshot,
} from "../model";
import {
  createQuotationFileName,
  lineAmountLabel,
  lineItemsForCadence,
  lineQuantityLabel,
  lineUnitPriceLabel,
} from "./shared";
import { createQuotationExportSnapshot } from "./snapshot";

const DOCUMENT_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const CELL_BORDER = {
  style: BorderStyle.SINGLE,
  size: 4,
  color: "D7D9DE",
};
const CELL_BORDERS = {
  top: CELL_BORDER,
  bottom: CELL_BORDER,
  left: CELL_BORDER,
  right: CELL_BORDER,
};

function textParagraph(
  text: string,
  options: {
    bold?: boolean;
    color?: string;
    size?: number;
    alignment?: (typeof AlignmentType)[keyof typeof AlignmentType];
  } = {},
): Paragraph {
  return new Paragraph({
    alignment: options.alignment ?? AlignmentType.LEFT,
    spacing: { after: 80 },
    children: [
      new TextRun({
        text,
        bold: options.bold ?? false,
        color: options.color ?? "17191D",
        size: options.size ?? 20,
        font: "Arial",
      }),
    ],
  });
}

function tableCell(
  text: string,
  width: number,
  alignment: (typeof AlignmentType)[keyof typeof AlignmentType] = AlignmentType.LEFT,
  bold = false,
): TableCell {
  return new TableCell({
    width: { size: width, type: WidthType.PERCENTAGE },
    borders: CELL_BORDERS,
    margins: { top: 90, bottom: 90, left: 90, right: 90 },
    children: [
      new Paragraph({
        alignment,
        children: [
          new TextRun({
            text,
            bold,
            font: "Arial",
            size: 18,
            color: "17191D",
          }),
        ],
      }),
    ],
  });
}

function itemTable(
  snapshot: QuotationExportSnapshot,
  cadence: BillingCadence,
): Array<Paragraph | Table> {
  const items = lineItemsForCadence(snapshot, cadence);
  if (items.length === 0) {
    return [];
  }

  const heading = cadence === "recurring" ? "Recurring charges" : "One-time charges";
  const header = new TableRow({
    tableHeader: true,
    children: [
      tableCell("Item", 46, AlignmentType.LEFT, true),
      tableCell("Qty / unit", 16, AlignmentType.LEFT, true),
      tableCell("Unit price", 18, AlignmentType.RIGHT, true),
      tableCell("Amount", 20, AlignmentType.RIGHT, true),
    ],
  });
  const rows = items.map(
    (lineItem) =>
      new TableRow({
        children: [
          tableCell(
            [lineItem.description, lineItem.details, lineItem.provenance.label]
              .filter((part) => part.trim().length > 0)
              .join("\n"),
            46,
          ),
          tableCell(lineQuantityLabel(lineItem), 16),
          tableCell(lineUnitPriceLabel(snapshot, lineItem), 18, AlignmentType.RIGHT),
          tableCell(lineAmountLabel(snapshot, lineItem), 20, AlignmentType.RIGHT),
        ],
      }),
  );

  return [
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 220, after: 100 },
      children: [
        new TextRun({
          text: heading,
          bold: true,
          font: "Arial",
          size: 22,
          color: "17191D",
        }),
      ],
    }),
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [header, ...rows],
    }),
  ];
}

function subtotalParagraphs(snapshot: QuotationExportSnapshot): Paragraph[] {
  const quotation = snapshot.quotation;
  const paragraphs: Paragraph[] = [];

  if (snapshot.totals.recurring.includedLineCount > 0) {
    const amount = snapshot.totals.recurring.authoritativeSubtotalMinor;
    paragraphs.push(
      textParagraph(
        `Recurring subtotal: ${
          amount === null
            ? "TBD"
            : formatMoney(amount, quotation.currency, quotation.currencyMinorUnits)
        }`,
        { bold: true, alignment: AlignmentType.RIGHT },
      ),
    );
  }

  if (snapshot.totals.oneTime.includedLineCount > 0) {
    const amount = snapshot.totals.oneTime.authoritativeSubtotalMinor;
    paragraphs.push(
      textParagraph(
        `One-time subtotal: ${
          amount === null
            ? "TBD"
            : formatMoney(amount, quotation.currency, quotation.currencyMinorUnits)
        }`,
        { bold: true, alignment: AlignmentType.RIGHT },
      ),
    );
  }

  paragraphs.push(
    textParagraph(
      `Grand total: ${
        snapshot.totals.grandTotalMinor === null
          ? "TBD"
          : formatMoney(
              snapshot.totals.grandTotalMinor,
              quotation.currency,
              quotation.currencyMinorUnits,
            )
      }`,
      {
        bold: true,
        size: 26,
        color: "8A6936",
        alignment: AlignmentType.RIGHT,
      },
    ),
  );

  return paragraphs;
}

function createDocxDocument(snapshot: QuotationExportSnapshot): Document {
  const quotation = snapshot.quotation;
  const sellerDetails = [
    quotation.seller.address,
    quotation.seller.email,
    quotation.seller.phone,
  ]
    .filter((value) => value.trim().length > 0)
    .join(" | ");
  const customerDetails = [
    quotation.customer.address,
    quotation.customer.email,
    quotation.customer.phone,
  ]
    .filter((value) => value.trim().length > 0)
    .join(" | ");
  const children: Array<Paragraph | Table> = [
    textParagraph(quotation.seller.name, {
      bold: true,
      size: 34,
    }),
    textParagraph(sellerDetails, { color: "5D626B", size: 17 }),
    textParagraph("QUOTATION", {
      bold: true,
      color: "8A6936",
      size: 42,
    }),
    textParagraph(`Quotation no.: ${quotation.quotationNumber}`),
    textParagraph(`Issue date: ${quotation.issueDate}`),
    textParagraph(`Valid until: ${quotation.validUntil}`),
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 180, after: 80 },
      children: [
        new TextRun({
          text: "Prepared for",
          bold: true,
          font: "Arial",
          size: 20,
        }),
      ],
    }),
    textParagraph(quotation.customer.name, { bold: true, size: 24 }),
    textParagraph(customerDetails, { color: "5D626B", size: 17 }),
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 220, after: 100 },
      children: [
        new TextRun({
          text: quotation.title,
          bold: true,
          font: "Arial",
          size: 30,
        }),
      ],
    }),
  ];

  if (quotation.introduction.trim().length > 0) {
    children.push(textParagraph(quotation.introduction));
  }

  children.push(...itemTable(snapshot, "recurring"));
  children.push(...itemTable(snapshot, "one-time"));
  children.push(...subtotalParagraphs(snapshot));

  if (quotation.terms.some((term) => term.trim().length > 0)) {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 220, after: 80 },
        children: [
          new TextRun({
            text: "Terms",
            bold: true,
            font: "Arial",
            size: 22,
          }),
        ],
      }),
    );
    for (const term of quotation.terms) {
      if (term.trim().length > 0) {
        children.push(
          new Paragraph({
            bullet: { level: 0 },
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: term.trim(),
                font: "Arial",
                size: 20,
              }),
            ],
          }),
        );
      }
    }
  }

  if (quotation.preparedBy.trim().length > 0) {
    children.push(
      textParagraph(`Prepared by: ${quotation.preparedBy}`, { bold: true }),
    );
  }
  if (quotation.closingNote.trim().length > 0) {
    children.push(textParagraph(quotation.closingNote, { color: "5D626B" }));
  }

  return new Document({
    title: "Quotation",
    subject: "Locally generated quotation",
    creator: "Quotation and QR Sender",
    lastModifiedBy: "Quotation and QR Sender",
    description: "Generated locally by Quotation and QR Sender",
    keywords: "quotation",
    revision: 1,
    sections: [
      {
        properties: {
          page: {
            size: {
              width: 12_240,
              height: 15_840,
              orientation: PageOrientation.PORTRAIT,
            },
            margin: {
              top: 900,
              right: 900,
              bottom: 900,
              left: 900,
            },
          },
        },
        children,
      },
    ],
  });
}

export async function generateQuotationDocx(
  quotation: Quotation,
  now = new Date(),
): Promise<GeneratedQuotationFile> {
  const snapshot = createQuotationExportSnapshot(quotation, now);
  const document = createDocxDocument(snapshot);
  const blob = await Packer.toBlob(document);

  return {
    name: createQuotationFileName(snapshot, "docx"),
    type: DOCUMENT_MIME,
    blob: new Blob([blob], { type: DOCUMENT_MIME }),
    snapshot,
  };
}
